import { Badge, Panel, TwoColumnLayout, VerticalContainer } from '@/components';
import { formatDateTime } from '@/lib/format';
import {
    ConsultationTypeEnum,
    PaymentProvider,
    getEnumTranslationByKey,
} from '@/shared/enums';

import { R } from '@/caregiverSpace/resources/Texts.resx';

import {
    IConsultationDetailsDto,
    PropertyObjectType,
    PropertyType,
} from '@/caregiverSpace/shared/model';
import { PropertiesList } from '@/caregiverSpace/shared/ui';

import { CouponView } from './CouponView';

type ConsultationPanelProps = {
    consultationDetails: IConsultationDetailsDto;
};

const getConsultationProperties = ({
    Consultation,
    CouponRedemption,
}: IConsultationDetailsDto) => {
    const isCompleted =
        !!Consultation.ConsultationVideoSchedules?.[0]?.FinishedOn;

    const leftProperties: PropertyObjectType[] = [
        { label: R.Id, value: Consultation.ID, type: PropertyType.TEXT },
        {
            label: R.Type,
            value: `${getEnumTranslationByKey(ConsultationTypeEnum, Consultation.ConsultationType)} ${Consultation.ConsultationVideoSchedules?.[0]?.IsQueued ? R.Queue : R.Appointment}`,
            type: PropertyType.TEXT,
        },
        ...(isCompleted
            ? [
                  {
                      label: R.Caregiver,
                      value: Consultation.Doctor?.User?.FullName,
                      type: PropertyType.TEXT,
                  },
              ]
            : []),
        {
            label: R.CanWeInformYourGP,
            value: Consultation.NotifyOwnDoctor ? R.Yes : R.No,
            type: PropertyType.TEXT,
        },
    ];

    const rightProperties: PropertyObjectType[] = [
        ...(isCompleted
            ? [
                  {
                      label: R.ScheduledOn,
                      value: formatDateTime(
                          Consultation.ConsultationVideoSchedules?.[0]
                              ?.ScheduledOn,
                      ),
                      type: PropertyType.TEXT,
                  },
                  {
                      label: R.StartTime,
                      value: formatDateTime(
                          Consultation.ConsultationVideoSchedules?.[0]
                              ?.StartedOn ??
                              Consultation.ConsultationVideoSchedules?.[0]
                                  ?.ScheduledOn,
                      ),
                      type: PropertyType.TEXT,
                  },
                  {
                      label: R.Completed,
                      value: formatDateTime(
                          Consultation.ConsultationVideoSchedules?.[0]
                              ?.FinishedOn,
                      ),
                      type: PropertyType.TEXT,
                  },
              ]
            : []),
        {
            label: R.PaymentProvider,
            value: (
                <>
                    {Consultation.Coupon && (
                        <CouponView
                            coupon={Consultation.Coupon}
                            couponRedemption={CouponRedemption}
                        />
                    )}
                    {!Consultation.Coupon &&
                        getEnumTranslationByKey(
                            PaymentProvider,
                            Consultation.PaymentVerificationData
                                ?.PaymentProvider,
                        )}
                </>
            ),
            type: PropertyType.TEXT,
        },
    ];

    if (Consultation.Rating) {
        rightProperties.push({
            label: R.Rating,
            value: Consultation.Rating,
            type: PropertyType.TEXT,
        });
    }

    return { leftProperties, rightProperties };
};

export const ConsultationPanel = ({
    consultationDetails,
}: ConsultationPanelProps) => {
    const { leftProperties, rightProperties } =
        getConsultationProperties(consultationDetails);

    return (
        <Panel>
            <VerticalContainer>
                <Badge type="default">{R.Consultation}</Badge>
                <TwoColumnLayout
                    leftColumn={<PropertiesList properties={leftProperties} />}
                    rightColumn={
                        <PropertiesList properties={rightProperties} />
                    }
                />
                {consultationDetails.Consultation.RatingComment && (
                    <VerticalContainer spacing="none" className="gap-1">
                        <span>{R.Comment}</span>
                        <span>
                            {consultationDetails.Consultation.RatingComment}
                        </span>
                    </VerticalContainer>
                )}
            </VerticalContainer>
        </Panel>
    );
};
